import { useQuery } from '@apollo/client';
import { ContactPageOutlined } from '@mui/icons-material';
import { Stack, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import { Widget, WidgetContent, WidgetHeader, WidgetIcon, useScrollIntoView } from '@procureai/ui-mui';
import { registerLocale, getName } from 'i18n-iso-countries';
import de from 'i18n-iso-countries/langs/de.json';
import en from 'i18n-iso-countries/langs/en.json';
import { isEmpty } from 'ramda';
import ReactCountryFlag from 'react-country-flag';
import { useTranslation } from 'react-i18next';

import { CaclCountryReportDimensionValue, CountryCode } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import { CircularProgressCentered } from '~/components/CircularProgressCentered/CircularProgressCentered';
import { DataGridCustom } from '~/components/DataGridCustom';
import { PlaceholderWidget } from '~/components/PlaceholderWidget/PlaceholderWidget';
import { useRiskCountryReport } from '~/modules';

import { COUNTRY_REPORT_TABLE_QUERY } from './CountryReportTableWidget.gql';

registerLocale(de);
registerLocale(en);

const WIDGET_NAME = 'CountryReportTableWidget';

const transformCountryReportTable = (values: CaclCountryReportDimensionValue[]) => {
  const countryCodeMap: { [key: string]: { [key: string]: number } } = {};

  values.forEach((value) => {
    const { countryCode, dimensionId, value: dimensionValue } = value;
    if (!countryCodeMap[countryCode]) {
      countryCodeMap[countryCode] = {};
    }
    countryCodeMap[countryCode][dimensionId] = dimensionValue;
  });

  const transformed = Object.entries(countryCodeMap).map(([countryCode, dimensionsMap]) => ({
    countryCode,
    ...dimensionsMap,
  }));

  return transformed;
};

export const CountryCell = ({ countryCode }: { countryCode: CountryCode }) => {
  const { i18n } = useTranslation();
  const validLng = i18n.language === 'dx' ? 'en' : i18n.language;
  return (
    <Stack
      direction="row"
      spacing={3}
      sx={{
        alignItems: 'center',
      }}
    >
      <ReactCountryFlag countryCode={countryCode} />
      <Typography>{getName(countryCode, validLng)}</Typography>
    </Stack>
  );
};

export const CountryReportTableWidget = () => {
  const { id } = useRiskCountryReport();
  const { t } = useTranslation(I18nNamespace.riskSustainability, { keyPrefix: WIDGET_NAME });
  const [widgetRef, active] = useScrollIntoView({ hash: 'risk-country-report-table' });
  const { data, loading } = useQuery(COUNTRY_REPORT_TABLE_QUERY, { variables: { id } });
  const report = data?.cataclysm?.countryReport;
  const rows = transformCountryReportTable(report?.values ?? []);
  const columns = (report?.dimensions ?? []).map(
    (dimension): GridColDef => ({
      field: dimension.id,
      headerName: dimension.name,
      width: 150,
    }),
  );

  return (
    <Widget ref={widgetRef} active={active} data-intercom-target={WIDGET_NAME}>
      <WidgetHeader title={t('title')} icon={<WidgetIcon icon={<ContactPageOutlined fontSize="small" />} />} />
      <WidgetContent sx={{ height: '100%' }}>
        {!isEmpty(rows) ? (
          <DataGridCustom
            columns={[
              {
                field: 'countryCode',
                headerName: 'Country',
                width: 150,
                renderCell: ({ row }) => <CountryCell countryCode={row.countryCode} />,
              },
              ...columns,
            ]}
            rows={rows}
            getRowId={(row) => row.countryCode}
            pagination={true}
            hideFooter={false}
          />
        ) : loading ? (
          <CircularProgressCentered sx={{ position: 'relative' }} />
        ) : (
          <PlaceholderWidget />
        )}
      </WidgetContent>
    </Widget>
  );
};
